import axios, { type AxiosError } from 'axios'
import { apiUrl } from '.'

export interface APIResponse<D = undefined> {
    data?: D
    message?: string
    error?: string
}

export class AuthSessionExpiredError extends Error {
    readonly __authSessionExpired = true

    constructor() {
        super('Session expired')
        this.name = 'AuthSessionExpiredError'
    }
}

/** Lazy-load store to avoid circular imports (api → store → reducers → actions → services → api). */
const getAuthToken = (): string => {
    const { store } = require('../redux/store') as typeof import('../redux/store')
    return store.getState().authUser?.['auth-token'] || ''
}

const AUTH_ERROR_MESSAGES = [
    'Signature verification failed',
    'Expired token',
    'Authorization header missing',
    'Authorization token missing',
    'Unknown user',
    'Wrong number of segments',
    'Invalid header encoding',
    'Invalid claims encoding',
]

let isHandlingAuthFailure = false

export const isAuthSessionExpiredError = (error: unknown): boolean => {
    if (isHandlingAuthFailure) {
        return true
    }

    if (error instanceof AuthSessionExpiredError) {
        return true
    }

    if (axios.isAxiosError(error) && isAuthFailure(error as AxiosError<APIResponse>)) {
        return true
    }

    return false
}

const isAuthFailure = (error: AxiosError<APIResponse>): boolean => {
    if (error.response?.status === 401) {
        return true
    }

    const apiError = error.response?.data?.error
    if (typeof apiError !== 'string') {
        return false
    }

    return AUTH_ERROR_MESSAGES.some((message) => apiError.includes(message))
}

const rejectAuthSessionExpired = () => Promise.reject(new AuthSessionExpiredError())

const handleAuthFailure = () => {
    if (isHandlingAuthFailure) {
        return
    }

    isHandlingAuthFailure = true

    const { store } = require('../redux/store') as typeof import('../redux/store')
    const { logoutUser } = require('../redux/actions/authUserActions') as typeof import('../redux/actions/authUserActions')
    store.dispatch(logoutUser())

    if (window.location.pathname !== '/login') {
        window.location.replace('/login')
        return
    }

    isHandlingAuthFailure = false
}

if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        if (isAuthSessionExpiredError(event.reason)) {
            event.preventDefault()
        }
    })
}

const axiosInstance = axios.create({
    baseURL: apiUrl,
})

axiosInstance.interceptors.request.use((config) => {
    if (isHandlingAuthFailure) {
        return rejectAuthSessionExpired()
    }

    const token = getAuthToken()
    if (token) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = token
    }
    return config
})

axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<APIResponse>) => {
        if (isAuthFailure(error)) {
            handleAuthFailure()
            return rejectAuthSessionExpired()
        }
        return Promise.reject(error)
    },
)

export const api = () => axiosInstance

export const errorHandler = (error: any, errorText?: string) => {

    if (isAuthSessionExpiredError(error)) {
        return ''
    }

    const defaultText = 'Unable to process request'
    if (error.code && error.code === 'ERR_NETWORK') {
        return String(error.message
            ? error.message
            : errorText ?
                errorText
                : defaultText)
    } else if (error.response) {
        return String(error.response.data
            ? error.response.data.message
            : errorText ?
                errorText
                : defaultText)
    } else {
        return String(error.message
            ? error.message
            : errorText ?
                errorText
                : defaultText)
    }
}

export default api